import { CollectionProductResponse } from 'api/product';
import { PersonalDiscountedVariant } from 'api/product/getPersonalizedProductDetails/types';
import { SmartCollectionItem, SmartCollectionResponse } from 'api/product/getSmartCollection/types';
import { isSmartCollectionItem } from 'api/product/getSmartCollection/utils';
import { SegmentationIds } from 'constants/segmentation';
import { SegmentationStore } from 'contexts/segmentation';
import isString from 'lodash/isString';
import { ProductSegmentation } from 'modules/product-details/types';
import { slugify } from 'utils/strings';
import getPersonalizedSubtitle from './utils/getPersonalizedSubtitle';

const sortVariants = (a: any, b: any) => +a.priceV2.amount - +b.priceV2.amount;

// net_content_quantity is expressed in the variant's own unit ('g' | 'kg'), so a 2kg
// variant arrives as 2 and a 400g variant as 400; normalize to grams so weight-based
// variant selection (dynamic bundle) compares them on the same scale.
const toGrams = (quantity: number, unit: string): number =>
  unit?.toLowerCase() === 'kg' ? quantity * 1000 : quantity;

type CollectionProductInput = Omit<
  CollectionProductResponse,
  '_updatedAt' | '_updated' | 'sk' | 'pk'
>;

function assertIsSmartCollectionProduct(
  product: unknown
): product is SmartCollectionResponse['items'][number] {
  if (typeof product !== 'object' || product === null) {
    return false;
  }

  const prod = product as SmartCollectionResponse['items'][number];

  if (typeof prod.subtitle !== 'string') {
    return false;
  }

  if (prod.bestseller !== undefined && typeof prod.bestseller === 'boolean') {
    return false;
  }

  return true;
}

export type Variant = Omit<CollectionProductInput['variants'][number], 'compareAtPrice'> & {
  compareAtPrice?: string;
};

enum MetafieldNameSpaces {
  Petsdeli = 'petsdeli',
  Subscription = 'subscription',
  Accentuate = 'accentuate',
  Rating = 'rating',
}

export type Metadata = {
  [x in MetafieldNameSpaces]: {
    [key in string]?: any;
  };
};

/**
 * Empty Object returned from BE for migration to PDEX
 * All the required information should be already assigned to product response
 */
interface EmptyMetadata {
  [MetafieldNameSpaces.Accentuate]: Record<string, never>;
}

type SearchProductInput = Omit<CollectionProductInput, 'variants' | 'metadata' | 'createdAt'> & {
  variants: Array<Variant>;
  metadata: Metadata;
};

export type CollectionVariant = Omit<
  CollectionProductResponse['variants'][number],
  'compareAtPrice' | 'id'
> & {
  id: string;
  /**
   * Price to be compared with discount price.
   * decimal is omitted from the value e.g 4999 meaning 49€ 99cent"
   * */
  compareAtPrice?: number;
};

interface CollectionProductOptions {
  currencyCode: string;
}

export interface AvailableVariant {
  variantId: string;
  available: boolean;
  comparePrice?: number;
  defaultPrice?: number;
  weight: number;
  packQuantity?: number;
  title?: string;
}

const generateBaseVariant = (product: SmartCollectionItem, options: CollectionProductOptions) => {
  return {
    availableForSale: product.available,
    image: {
      alt: product.image_alt || undefined,
      src: product.image_src,
    },
    weight: Number(product.net_grams),
    id: product.variant_id,
    title: product.variant_title,
    sku: product.sku,
    priceV2: {
      currencyCode: options.currencyCode,
      amount: product.price,
    },
    weightUnit: 'GRAMS' as const,
    compareAtPrice: product.compare_price
      ? Math.round(parseFloat(product.compare_price) * 100)
      : undefined,
  };
};

/** Common Product interface */
export class CollectionProduct {
  public id: string;
  public productType: string;
  public productId: string;
  public title: string;
  public tags: string[];
  public handle: string;
  /** Make it private to hide implementation  */
  private metadata: Metadata | EmptyMetadata = {
    [MetafieldNameSpaces.Accentuate]: {},
    [MetafieldNameSpaces.Subscription]: {},
    [MetafieldNameSpaces.Rating]: {},
    [MetafieldNameSpaces.Petsdeli]: {},
  };
  public availableForSale: boolean;
  public variants: Array<CollectionVariant>;
  public baseVariant?: CollectionVariant;
  public variantsAvailability?: Record<string, boolean>;
  public variantsAvailabilityList?: AvailableVariant[];
  public image: Partial<CollectionProductResponse['image']> = {};

  /** This property is assigned when a variant is assigned as product to promote a specific variant on dashboard */
  public promoteVariantId?: string;

  public createdAt: string;

  private segmentation?: ProductSegmentation;

  private ribbonText?: string;
  private ribbonColor?: string;
  /** Sub title for product which is generated from segmentation input */
  public subTitle?: string | React.ReactNode;
  /** Total score of a product score calculated by logics */
  public totalRecommendationScore?: number;

  private isBestSeller?: boolean;
  private isStaticCollectionItem?: boolean;
  public variantLength: number;
  /** Array of variant title */
  private variantTitles?: Array<string>;
  private isSmartCollectionItem: boolean;

  public variantPrice?: PersonalDiscountedVariant;

  public getIsSmartCollectionItem = (): boolean => {
    return this.isSmartCollectionItem;
  };

  public getSlugifiedTags = (): Array<string> => {
    return this.tags.map(slugify);
  };

  public getProductTitle = (): string => {
    return this.title || this.metadata.accentuate.mainTitle;
  };

  /**
   * get subtitle for collection item
   * Since for cat collection, BE can't generate subtitle, subtitle is decided based on tags
   */
  public getProductSubtitle = ({
    segmentation,
  }: {
    segmentation?: SegmentationIds;
  } = {}): string | React.ReactNode | undefined => {
    if (!this.isStaticCollectionItem) {
      return this.subTitle;
    }
    if (segmentation) {
      const subtitle = getPersonalizedSubtitle({
        segmentation,
        tags: this.tags,
      });
      if (subtitle) return subtitle;
    }
    return this.subTitle ?? this.metadata.accentuate.subTitle;
  };

  public getRibbon = () => {
    if (this.metadata['accentuate'].ribbonText1) {
      return {
        ribbonText: this.metadata['accentuate'].ribbonText1,
        ribbonText2: this.metadata['accentuate'].ribbonText2,
        ribbonColor: this.metadata['accentuate'].ribbonColor,
      };
    } else {
      return {
        ribbonText: this.ribbonText,
        ribbonColor: this.ribbonColor,
      };
    }
  };

  public getIsBestSeller = ({
    segment,
  }: {
    segment?: SegmentationIds | SegmentationStore;
  } = {}): boolean => {
    if (this.segmentation && segment) {
      const key = typeof segment === 'string' ? segment : segment.lastSegmentation;
      if (key && this.segmentation && this.segmentation[key]) {
        return !!this.segmentation[key].isBestSeller;
      }
    }
    return !!this.isBestSeller;
  };

  /**
   * Check if from price e.g "ab 19.99" should be shown
   */
  public getShouldShowFromPrice = (): boolean => {
    return this.variantLength > 1 || this.variants.length > 1;
  };

  /**
   * Retrieve weight string from variant title.
   * Variant title are conventionally named in the form of "[weight per one product] / [packing unit]"
   * e.g "200g / Einzeldose" , "Huhn / 12 x 400g"
   */
  public getWeights = (): string[] => {
    const titles = this.isSmartCollectionItem
      ? (this.variantTitles ?? [])
      : this.variants.map((v) => v.title);
    const trimmed = titles.map((t) => t.split('/')[0].trim());

    if (this.isSmartCollectionItem) {
      return Array.from(new Set(trimmed));
    }

    return trimmed.filter(
      (weight: string, index: number) =>
        index === this.variants.findIndex((variant) => variant.title.includes(weight))
    );
  };

  /** Returns the cheapest variant */
  public getCheapestVariant = () => {
    const _variants = [...this.variants];
    return _variants.sort(sortVariants)[0];
  };

  /** Returns the first cheapest subscribable variant (if available) */
  public getFirstCheapestAvailableSubsVariant = (): CollectionVariant | null => {
    const _tags = [...this.tags];
    const _variants = [...this.variants];
    const variant = _variants
      .filter((variant) => variant.availableForSale)
      .sort((a, b) => +a.priceV2.amount - +b.priceV2.amount)[0];
    return _tags.includes('SubscriptionEnabled') ? variant : null;
  };

  private static options: CollectionProductOptions;

  public static setOptions(options: CollectionProductOptions): void {
    CollectionProduct.options = options;
  }

  constructor(
    product: CollectionProductInput | SearchProductInput | SmartCollectionResponse['items'][number]
  ) {
    this.isSmartCollectionItem = false;
    if (isSmartCollectionItem(product)) {
      this.isSmartCollectionItem = true;
      this.variantTitles = product.variant_titles;
      this.id = product.product_id;
      this.productType = product.product_category;
      this.title = product.product_title;
      this.variantPrice = product.variant_price;
      this.variantsAvailabilityList = product.variants_availability.map((item, index) => ({
        variantId: item.variant_id,
        available: item.available,
        defaultPrice: Math.round(parseFloat(item.default_price) * 100),
        weight: toGrams(item.net_content_quantity, item.net_content_unit),
        packQuantity: item.pack_quantity ?? 1,
        title: product.variant_titles?.[index],
      }));
      this.variantsAvailability = product.variants_availability?.reduce((acc, item) => {
        acc[item.variant_id] = item.available;
        return acc;
      }, {});

      try {
        // Backend returns bold tag as "** some word** "
        const formattedText = product.subtitle.replace(
          /\*\*(.*?)\*\*/g,
          '<span class="font-medium">$1</span>'
        );
        this.subTitle = <span dangerouslySetInnerHTML={{ __html: formattedText }} />;
      } catch (error) {
        this.subTitle = product.subtitle;
      }

      this.tags = product.tags;
      this.handle = product.slug;
      this.productId = product.product_id.toString();
      this.metadata = { [MetafieldNameSpaces.Accentuate]: {} };
      this.availableForSale = product.available;
      this.image.src = product.image_src;
      this.image.alt = product.image_alt || undefined;
      // TODO: this called variants, but it is an array with only 1 element. So either it is obsolete/wrong either it should be called in some other way
      const baseVariant = generateBaseVariant(product, CollectionProduct.options);
      this.variants = [baseVariant];
      this.baseVariant = baseVariant;
      this.createdAt = '0';
      this.ribbonText = product.ribbon?.text;
      this.ribbonColor = product.ribbon?.color;
      this.isBestSeller = product.bestseller;
      this.variantLength = product.variant_length;
    } else {
      this.id = product.masterProductId ? product.masterProductId + '' : product.id + '';
      this.productType = product.productType;
      this.title = product.title || product.metadata.accentuate?.mainTitle;
      this.tags = product.tags;
      this.handle = product.handle;
      this.productId = product.productId;
      this.metadata = product.metadata;
      this.availableForSale = product.variants.some((variant) => variant.availableForSale);
      /**
       * Due to the different implementation on BE, compareAtPrice could be number e.g 4999 or string e.g "49.99"
       * Therefore we align it to be number type without decimal.
       */
      this.variants = product.variants.map(
        (variant: Variant | CollectionProductInput['variants'][number]) => {
          const compareAtPrice = variant.compareAtPrice
            ? isString(variant.compareAtPrice)
              ? +variant.compareAtPrice * 100
              : variant.compareAtPrice
            : undefined;
          return {
            ...variant,
            id: variant.id + '',
            compareAtPrice,
          };
        }
      );
      this.variantLength = product.variants.length;
      this.image.src = product.image?.src ?? '';
      this.image.alt = product.image?.alt ?? undefined;
      this.createdAt = product['createdAt'] ?? '0';
      this.promoteVariantId = product.masterProductId ? product.id + '' : undefined;
    }

    if (!assertIsSmartCollectionProduct(product)) {
      this.isStaticCollectionItem = true;
      if (product.segmentation && Object.values(product.segmentation).length > 0) {
        this.segmentation = product.segmentation;
      }
    }
  }
}
